import { useEffect, useRef, useState } from "react";

/*
 * This hook is used to calculate the size and position of the canvas
 * so we can replicate the CSS Object-Fit "contain" behavior in a canvas
 *
 * We use the ratio to scale the image to fit inside the container div and to
 * scale up and down the regions before saving them/ drawing
 */
export const useCanvasContain = (assetSize: { width: number; height: number }) => {
  /* Refs */
  const editorRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0, y: 0 });

  /* State */
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [ratio, setRatio] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  /* Side Effects */
  useEffect(() => {
    if (!assetSize) return;
    if (!editorRef.current) return;

    const w = editorRef.current.clientWidth;
    const h = editorRef.current.clientHeight;

    /*
      calculate the horizontal and vertical ratio of the
      original image dimensions versus the container div
    */
    const hRatio = w / assetSize.width;
    const vRatio = h / assetSize.height;

    /*
      to replicate the CSS Object-Fit "contain" behavior,
      choose the smaller of the horizontal and vertical
      ratios
    
      if you want a "cover" behavior, use Math.max to
      choose the larger of the two ratios instead
    */
    const ratio = Math.min(hRatio, vRatio);

    setRatio(ratio);

    /*
      calculate the offsets so the image is centered inside
      the stage
    */
    const xOffset = (w - assetSize.width * ratio) / 2;
    const yOffset = (h - assetSize.height * ratio) / 2;

    setCanvasSize({
      width: assetSize.width * ratio,
      height: assetSize.height * ratio,
    });

    setPosition({
      x: xOffset,
      y: yOffset,
    });

    posRef.current = {
      x: xOffset,
      y: yOffset,
    };
  }, [assetSize, editorRef]);

  /* Render */
  return { canvasSize, ratio, position, editorRef, setPosition, posRef };
};
